import { getFragmentData } from '__generated__';
import { TimezoneDisplayPreference, type UpdateUserSettingsInput } from '__generated__/graphql';
import { TENANT_CONFIG } from 'graphql/tenants/fragments/TenantConfig.gql';
import { useGetOneTenantQuery } from 'hooks/tenants/queries/useGetOneTenantQuery';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuthContext } from './auth.context';

export type DisplayTimezoneMode = NonNullable<UpdateUserSettingsInput['timezoneDisplayPreference']>;

interface DisplayTimezoneContextInterface {
  displayTimezoneMode: DisplayTimezoneMode;
  resolvedDisplayTimezone?: string;
  setDisplayTimezoneMode: (mode: DisplayTimezoneMode) => void;
}

const defaultTimezoneMode = TimezoneDisplayPreference.Utc;
const DISPLAY_TIMEZONE_STORAGE_KEY = 'restore.user.displayTimezonePreference';

const DisplayTimezoneContext = createContext<DisplayTimezoneContextInterface>({
  displayTimezoneMode: defaultTimezoneMode,
  resolvedDisplayTimezone: 'UTC',
  setDisplayTimezoneMode: () => {},
});

export const DisplayTimezoneContextProvider = ({ children }: { children: ReactNode }) => {
  const { user, userTenantId, userTenantTimezone } = useAuthContext();
  // The auth-context tenant timezone is hydrated from login/localStorage and can
  // go stale if the tenant's zone is changed server-side. Fetch the live tenant
  // with cache-and-network so a server-side change always revalidates the
  // normalized cache rather than returning the stale login-time value.
  const { tenant: tenantRef } = useGetOneTenantQuery({
    id: userTenantId ?? 0,
    fetchPolicy: 'cache-and-network',
  });
  const tenant = getFragmentData(TENANT_CONFIG, tenantRef);
  const effectiveTenantTimezone = tenant?.timezone ?? userTenantTimezone;
  const userWithTimezonePreference = user?.user;
  const userId = userWithTimezonePreference?.id ? String(userWithTimezonePreference.id) : undefined;
  const serverTimezoneDisplayPreference =
    userWithTimezonePreference?.timezoneDisplayPreference ?? defaultTimezoneMode;

  const readStoredTimezoneMode = (currentUserId?: string): DisplayTimezoneMode | undefined => {
    if (!currentUserId) {
      return undefined;
    }
    try {
      const stored = localStorage.getItem(DISPLAY_TIMEZONE_STORAGE_KEY);
      if (!stored) {
        return undefined;
      }
      const parsed: unknown = JSON.parse(stored);
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        !('userId' in parsed) ||
        parsed.userId !== currentUserId ||
        !('mode' in parsed)
      ) {
        return undefined;
      }
      return parsed.mode === TimezoneDisplayPreference.Utc ||
        parsed.mode === TimezoneDisplayPreference.Locale ||
        parsed.mode === TimezoneDisplayPreference.Tenant ||
        parsed.mode === TimezoneDisplayPreference.User
        ? parsed.mode
        : undefined;
    } catch {
      return undefined;
    }
  };

  const storeTimezoneMode = (currentUserId: string, mode: DisplayTimezoneMode) => {
    localStorage.setItem(
      DISPLAY_TIMEZONE_STORAGE_KEY,
      JSON.stringify({
        userId: currentUserId,
        mode,
      })
    );
  };

  const [displayTimezoneMode, setDisplayTimezoneModeState] = useState<DisplayTimezoneMode>(() => {
    const storedMode = readStoredTimezoneMode(userId);
    return storedMode ?? serverTimezoneDisplayPreference;
  });

  const setDisplayTimezoneMode = useCallback(
    (mode: DisplayTimezoneMode) => {
      setDisplayTimezoneModeState(mode);
      if (userId) {
        storeTimezoneMode(userId, mode);
      }
    },
    [userId]
  );

  useEffect(() => {
    const storedMode = readStoredTimezoneMode(userId);
    if (storedMode) {
      setDisplayTimezoneModeState(storedMode);
      return;
    }
    setDisplayTimezoneModeState(serverTimezoneDisplayPreference);
  }, [serverTimezoneDisplayPreference, userId]);

  const resolvedDisplayTimezone = useMemo(() => {
    switch (displayTimezoneMode) {
      case TimezoneDisplayPreference.Utc:
        return 'UTC';
      case TimezoneDisplayPreference.Tenant:
        return effectiveTenantTimezone ?? 'UTC';
      case TimezoneDisplayPreference.User:
        return userWithTimezonePreference?.timezone ?? effectiveTenantTimezone ?? 'UTC';
      case TimezoneDisplayPreference.Locale:
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
      default:
        return 'UTC';
    }
  }, [displayTimezoneMode, effectiveTenantTimezone, userWithTimezonePreference?.timezone]);

  const contextValue = useMemo(
    () => ({ displayTimezoneMode, resolvedDisplayTimezone, setDisplayTimezoneMode }),
    [displayTimezoneMode, resolvedDisplayTimezone, setDisplayTimezoneMode]
  );

  return (
    <DisplayTimezoneContext.Provider value={contextValue}>
      {children}
    </DisplayTimezoneContext.Provider>
  );
};

export const useDisplayTimezoneContext = () => useContext(DisplayTimezoneContext);
